import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Paper,
} from '@mui/material';
import apiService from '../services/api';

interface Macro {
  id: number;
  name: string;
  description: string;
  content: string;
}

interface MacroSearchModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (macro: Macro) => void;
  anchorEl: HTMLElement | null;
}

const DIALOG_HEIGHT = 400;
const DIALOG_WIDTH = 400;

const MacroSearchModal: React.FC<MacroSearchModalProps> = ({
  open,
  onClose,
  onSelect,
  anchorEl,
}) => {
  const [macros, setMacros] = useState<Macro[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchMacros = async () => {
      setLoading(true);
      try {
        const data = await apiService.getMacros(1, 100); // Get up to 100 macros for the dropdown
        console.log('Fetched macros from API:', data);
        setMacros(data.macros || []);
      } catch (error) {
        console.error('Error fetching macros:', error);
        // Fallback to sample macros for testing
        const sampleMacros = [
          {
            id: 1,
            name: "Normal Skin Histology",
            description: "Standard template for normal skin biopsy findings",
            content: "GROSS DESCRIPTION:\\nThe specimen consists of an ellipse of skin measuring [prompt:dimensions] cm. The epidermis is intact and unremarkable.\\n\\nMICROSCOPIC DESCRIPTION:\\nSections show normal skin with intact epidermis and dermis. No significant pathologic changes are identified.\\n\\nDIAGNOSIS:\\nSkin, [prompt:site] - No significant pathologic changes identified."
          },
          {
            id: 2,
            name: "Clinical History Template", 
            description: "Standard clinical history introduction",
            content: "The patient is a [prompt:age]-year-old [prompt:gender] with a history of [prompt:medical history] who presents with [prompt:chief complaint]."
          },
          {
            id: 3,
            name: "Negative Biopsy",
            description: "Template for negative biopsy results",
            content: "MICROSCOPIC DESCRIPTION:\\nSections show [prompt:tissue type] with no significant pathologic changes. The architecture is preserved and no malignancy is identified.\\n\\nDIAGNOSIS:\\n[prompt:site], biopsy - No significant pathologic changes identified."
          },
          {
            id: 4,
            name: "Chronic Inflammation",
            description: "Template for chronic inflammatory findings",
            content: "MICROSCOPIC DESCRIPTION:\\nSections show chronic inflammation characterized by lymphocytes, plasma cells, and occasional eosinophils. [prompt:additional findings]\\n\\nDIAGNOSIS:\\n[prompt:site] - Chronic inflammation."
          }
        ];
        setMacros(sampleMacros);
      }
      setLoading(false);
    };

    if (open) {
      fetchMacros();
    }
  }, [open]);

  const filteredMacros = macros
    .filter(macro => 
      macro.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      macro.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleSelect = (macro: Macro) => {
    onSelect(macro);
    onClose();
  };

  if (!anchorEl) return null;

  const anchorRect = anchorEl.getBoundingClientRect();
  const spaceBelow = window.innerHeight - anchorRect.bottom;
  const spaceAbove = anchorRect.top;
  let top = anchorRect.bottom + 5;
  // If not enough space below, show above the anchor
  if (spaceBelow < DIALOG_HEIGHT && spaceAbove > DIALOG_HEIGHT) {
    top = anchorRect.top - DIALOG_HEIGHT - 5;
  } else if (spaceBelow < DIALOG_HEIGHT) {
    // Clamp to fit in viewport
    top = Math.max(10, window.innerHeight - DIALOG_HEIGHT - 10);
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          position: 'absolute',
          left: anchorRect.left,
          top,
          width: DIALOG_WIDTH,
          maxHeight: DIALOG_HEIGHT,
        },
      }}
    >
      <DialogTitle>Search Macros</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          placeholder="Search macros..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
        />
        <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
          {loading ? (
            <ListItem>
              <ListItemText primary="Loading macros..." />
            </ListItem>
          ) : (
            filteredMacros.map((macro) => (
              <ListItem
                key={macro.id}
                button
                onClick={() => handleSelect(macro)}
                sx={{ cursor: 'pointer' }}
              >
                <ListItemText
                  primary={macro.name}
                  secondary={macro.description}
                  primaryTypographyProps={{ fontWeight: 'medium' }}
                />
              </ListItem>
            ))
          )}
          {filteredMacros.length === 0 && (
            <ListItem>
              <ListItemText
                primary="No macros found"
                secondary={searchTerm ? "Try a different search term" : "No macros available"}
              />
            </ListItem>
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
};

export default MacroSearchModal; 